import { stableFilters } from './_filters';

type Id = string | number;

/** The import-run list/summary filters as they reach a key (the page size never does). */
type ImportRunsKeyFilters = {
  days?: number;
  status?: string;
  sourceSystem?: string;
  failureFamily?: string;
};

const IMPORT_RUNS_ROOT = ['superadmin', 'import-runs'] as const;

/**
 * Platform-admin (superadmin) query keys (Server-state rebuild, P4-tail). Flat roots under
 * the `superadmin` segment, preserving today's exact values.
 *
 * Import runs hang under ONE root so the page's manual Refresh invalidates the list (every
 * loaded page), the summary, the validation summary and any open run detail together. Filters
 * ride through `stableFilters`, so the unfiltered key stays a prefix of every filtered one.
 */
export const adminKeys = {
  aiConfig: () => ['superadmin', 'ai-config'] as const,
  teams: () => ['superadmin', 'teams'] as const,
  users: (tab?: string) =>
    tab ? (['superadmin', 'users', tab] as const) : (['superadmin', 'users'] as const),
  companies: (tab?: string) =>
    tab ? (['superadmin', 'companies', tab] as const) : (['superadmin', 'companies'] as const),
  company: (type: string, id: Id) => ['superadmin', 'company', type, String(id)] as const,
  vouchers: () => ['superadmin', 'vouchers'] as const,
  importRuns: () => IMPORT_RUNS_ROOT,
  importRunsList: (filters?: ImportRunsKeyFilters) => {
    const stable = stableFilters(filters);
    return stable
      ? ([...IMPORT_RUNS_ROOT, 'list', stable] as const)
      : ([...IMPORT_RUNS_ROOT, 'list'] as const);
  },
  importRunsSummary: (filters?: Pick<ImportRunsKeyFilters, 'days'>) => {
    const stable = stableFilters(filters);
    return stable
      ? ([...IMPORT_RUNS_ROOT, 'summary', stable] as const)
      : ([...IMPORT_RUNS_ROOT, 'summary'] as const);
  },
  importRunsValidationSummary: (filters?: Pick<ImportRunsKeyFilters, 'days'>) => {
    const stable = stableFilters(filters);
    return stable
      ? ([...IMPORT_RUNS_ROOT, 'validation-summary', stable] as const)
      : ([...IMPORT_RUNS_ROOT, 'validation-summary'] as const);
  },
  importRun: (jobId: Id) => [...IMPORT_RUNS_ROOT, 'detail', String(jobId)] as const,
} as const;
